/**
 * 模块说明
 * -------
 * 可打断请求控制器
 *
 * 用途：列表页在检索栏改动后发起新一轮检索时，打断上一轮还在飞的请求，
 * 避免旧响应晚到覆盖新结果。
 * 独立于 Page 基类，供未继承基类的页面（React.Component / 函数组件）使用。
 */

/**
 * 可打断请求控制器
 * Interruptible request controller.
 */
export class Interruptible {
    // 各 key 当前未完成请求的 controller
    private controllers: Record<string, AbortController> = {};

    /**
     * 开始一次可打断的请求：同一 key 的上一次未完成请求会被 abort
     * 不同 key 互不打断（如列表请求与树形展开请求）
     * @param key 区分用途，如 'list'
     */
    signal = (key: string): AbortSignal | undefined => {
        // 老浏览器没有 AbortController（whatwg-fetch 也不支持 signal）：降级为不打断
        // No AbortController in old browsers (and whatwg-fetch ignores signal): fall back to no interruption.
        if (typeof AbortController === 'undefined') return undefined;
        this.controllers[key]?.abort();
        const controller = new AbortController();
        this.controllers[key] = controller;
        return controller.signal;
    };

    /**
     * 中断所有 key 以该前缀开头的未完成请求（如 'expand:' 会中断 'expand:uuid1'、'expand:uuid2'）
     * Aborts every in-flight request whose key starts with the prefix
     * (e.g. 'expand:' aborts 'expand:uuid1' and 'expand:uuid2').
     * @param prefix key 前缀
     */
    abortByPrefix = (prefix: string) => {
        const remaining: Record<string, AbortController> = {};
        Object.keys(this.controllers).forEach((key) => {
            if (key.startsWith(prefix)) {
                this.controllers[key].abort();
            } else {
                remaining[key] = this.controllers[key];
            }
        });
        this.controllers = remaining;
    };

    /**
     * 中断所有还在飞的请求（页面卸载时调用）
     * Aborts every in-flight request (call on unmount).
     */
    abortAll = () => {
        Object.values(this.controllers).forEach((controller) => {
            controller.abort();
        });
        this.controllers = {};
    };
}
